import React, { ChangeEvent, useContext } from 'react';
import { Checkbox } from '@core-ui/checkbox';
import { Radio } from '@core-ui/radio';
// @ts-ignore
import { RailContext } from '@cat/rail';
import { ColorSwatch } from '@core-ui/color-swatch';
import { BreakpointContext, MEDIUM } from '@core-ui/breakpoint-provider';
import { isMobile } from 'react-device-detect';
import { CSSObject, getFontWeight, Theme, useTheme } from '@core-ui/react-stitch';
import {FeatureFlagsContext} from "@core-ui/feature-flags";
// @ts-ignore
import { StarRatingFacet } from '../StarRatingFacet/StarRatingFacet';

export type FacetItemProps = {
  facetDisplay: 'checkbox' | 'radio' | 'swatch' | 'ratings',
  applied: boolean,
  option: {
    id: string,
    name: string,
    value: string
  },
  facetName: string,
  isBRRedesignEnabled?: boolean,
  isMobile?: boolean,
  key?: string,
  position?: string,
};

const getBackground = (color: string) => {
  switch (color.toLowerCase()) {
    case 'multi':
      return 'linear-gradient(to right, red, orange, yellow, green, blue, purple)';
    default:
      return color.toLowerCase();
  }
};

// eslint-disable-next-line complexity
const FacetItem = ({
  facetDisplay,
  isBRRedesignEnabled,
  applied,
  option,
  facetName,
}: FacetItemProps) => {
  const { onFacetChange } = useContext(RailContext);
  const { minWidth } = useContext(BreakpointContext);
  const theme = useTheme();
  const { enabledFeatures } = useContext(FeatureFlagsContext);
  
  const customRadioStyles = {
    labelText: isBRRedesignEnabled ? { color: theme.color.b2 } : undefined,
    radioButton: isBRRedesignEnabled ? { borderColor: theme.color.b2 } : undefined,
  };

  const getBRRedesignStyle = (isBRRedesignEnabled: boolean): CSSObject => ({
    'label': {
      marginBottom: isBRRedesignEnabled ? '1rem' : '1.5rem',
    }
  });

  const displayWrapperStyle = (): CSSObject => ({
    textTransform: 'capitalize',
    ...(minWidth(MEDIUM) || isBRRedesignEnabled ? {} : { paddingLeft: '1rem', paddingRight: '1rem' }),
    ...getBRRedesignStyle(!!isBRRedesignEnabled),
  });

  const displayItemStyle = (theme: Theme): CSSObject => ({
    display: 'flex',
    justifyContent: 'center',
    width: '33%',
    marginBottom: '1rem',
    ...isBRRedesignEnabled && {
      'label': {
        color: theme.color.b2,
        fontWeight: getFontWeight('regular').fontWeight
      },
    }
  });

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFacetChange && onFacetChange({
      facetName,
      id: option.id,
      name: option.name,
      applied: event.currentTarget.checked === true,
    });
  };
  
  const shouldEnableInternalLinkURL = () => {
    return (
      enabledFeatures['seo-internal-link'] &&
      ["style", "color"].includes(facetName)
    );
  }

  const createInternalLinkURL = () => {
    if (!shouldEnableInternalLinkURL()) {
      return undefined;
    }
    const {origin, pathname, search} = window.location;
    const searchParams = new URLSearchParams(search);
  
    if (facetName === "color") {
      searchParams.set(facetName, option.value);
    } else {
      searchParams.set(facetName, option.id);
    }
  
    return new URL(`${origin}${pathname}?${searchParams}`);
  }
  
  const internalLink = createInternalLinkURL();

  switch (facetDisplay) {
    case 'checkbox':
      return (
        <div css={displayWrapperStyle}>
          <Checkbox
            isChecked={applied}
            labelText={option.name}
            onChange={onChange}
            url={internalLink}
          />
        </div>
      );
    case 'radio':
      return (
        <div css={displayWrapperStyle}>
          <Radio
            checked={applied}
            customBackgroundColor={isBRRedesignEnabled ? theme.color.b2 : ''}
            id={option.id}
            isRadioSmall={isBRRedesignEnabled}
            label={option.name}
            name={facetName}
            onChange={onChange}
            styles={customRadioStyles}
          />
        </div>
      );
    case 'ratings':
      return (
        <StarRatingFacet
          applied={applied}
          facetName={facetName}
          isMobile={isMobile}
          name={option.name}
          onChange={onChange}
          value={option.id}
        />
      );
    case 'swatch':
      return (
        <div css={displayItemStyle}>
          <ColorSwatch
            className="facetbar-item-colorswatch"
            colorName={option.name}
            customColor={getBackground(option.value)}
            id={option.id}
            link={internalLink}
            onChange={onChange}
            selected={applied}
            showColorName
            type="checkbox"
            url=""
            value={option.value}
          />
        </div>
      );
    default:
      return null;
  }
};

export default FacetItem;
